export type GuidedSetupProgressStatus = 'in_progress' | 'completed'

export interface GuidedSetupProgress {
  version: 1
  interfaceId: number
  status: GuidedSetupProgressStatus
  resetCompletedAt: string
  activeStepKey: string
  visitedStepKeys: string[]
  updatedAt: string
}

const STORAGE_PREFIX = 'guided-setup:v1:'

const storageKey = (interfaceId: number) => `${STORAGE_PREFIX}${interfaceId}`

const canUseStorage = () => typeof window !== 'undefined'

export const loadGuidedSetupProgress = (
  interfaceId: number,
): GuidedSetupProgress | null => {
  if (!canUseStorage() || !Number.isFinite(interfaceId)) return null

  try {
    const raw = window.localStorage.getItem(storageKey(interfaceId))
    if (!raw) return null

    const progress = JSON.parse(raw) as Partial<GuidedSetupProgress>
    if (
      progress.version !== 1 ||
      progress.interfaceId !== interfaceId ||
      (progress.status !== 'in_progress' && progress.status !== 'completed') ||
      typeof progress.resetCompletedAt !== 'string' ||
      typeof progress.activeStepKey !== 'string' ||
      !Array.isArray(progress.visitedStepKeys) ||
      typeof progress.updatedAt !== 'string'
    ) {
      window.localStorage.removeItem(storageKey(interfaceId))
      return null
    }

    if (!progress.visitedStepKeys.every((stepKey) => typeof stepKey === 'string')) {
      window.localStorage.removeItem(storageKey(interfaceId))
      return null
    }

    return progress as GuidedSetupProgress
  } catch {
    return null
  }
}

const saveGuidedSetupProgress = (progress: GuidedSetupProgress) => {
  if (!canUseStorage()) return progress
  try {
    window.localStorage.setItem(
      storageKey(progress.interfaceId),
      JSON.stringify(progress),
    )
  } catch {
    // The current session can continue even when browser storage is unavailable.
  }
  return progress
}

export const beginGuidedSetupProgress = (
  interfaceId: number,
  firstStepKey: string,
): GuidedSetupProgress => {
  const now = new Date().toISOString()
  return saveGuidedSetupProgress({
    version: 1,
    interfaceId,
    status: 'in_progress',
    resetCompletedAt: now,
    activeStepKey: firstStepKey,
    visitedStepKeys: [firstStepKey],
    updatedAt: now,
  })
}

export const updateGuidedSetupProgress = (
  progress: GuidedSetupProgress,
  activeStepKey: string,
  visitedStepKeys: Iterable<string>,
): GuidedSetupProgress =>
  saveGuidedSetupProgress({
    ...progress,
    activeStepKey,
    visitedStepKeys: Array.from(new Set(visitedStepKeys)),
    updatedAt: new Date().toISOString(),
  })

export const completeGuidedSetupProgress = (
  progress: GuidedSetupProgress,
  activeStepKey: string,
  visitedStepKeys: Iterable<string>,
): GuidedSetupProgress =>
  saveGuidedSetupProgress({
    ...progress,
    status: 'completed',
    activeStepKey,
    visitedStepKeys: Array.from(new Set(visitedStepKeys)),
    updatedAt: new Date().toISOString(),
  })

export const clearGuidedSetupProgress = (interfaceId: number) => {
  if (!canUseStorage()) return
  try {
    window.localStorage.removeItem(storageKey(interfaceId))
  } catch {
    // Storage may be disabled by the browser; there is no local marker to clear.
  }
}

/**
 * Every local key the guided setup writes for one interface.
 *
 * Matched by prefix rather than listed key by key: the decoder progress is per
 * tab, and a step added later gets swept along with the rest instead of being
 * left behind as a stale marker nobody remembers to clear.
 */
const GUIDED_STORAGE_PREFIXES = [
  STORAGE_PREFIX, // guided-setup:v1:<id>
  'guided-decoders:v1:', // guided-decoders:v1:<id>:<tab>
  'cisgenics.guided.stationDrafts.', // …stationDrafts.<id>
]

/**
 * Wipes the wizard's local state for an interface — the step the user was on,
 * which decoder rows they had edited, and any station rows staged but not yet
 * writable.
 *
 * Called when the interface's data is reset, from the admin list or from the
 * wizard's own reset step: the server no longer holds what these markers
 * describe, and left behind they resume a setup into data that is gone —
 * "step 4 of 7" on an empty controller, rows marked done that no longer exist.
 */
export const clearGuidedSetupStorage = (interfaceId: number | string) => {
  if (!canUseStorage()) return
  const id = String(interfaceId)
  if (!id || id === 'undefined') return

  const belongsToInterface = (key: string) =>
    GUIDED_STORAGE_PREFIXES.some((prefix) => {
      if (!key.startsWith(`${prefix}${id}`)) return false
      // Interface 1 must not carry off interface 12's keys: what follows the
      // id has to be the end of the key or the next separator.
      const rest = key.slice(prefix.length + id.length)
      return rest === '' || rest.startsWith(':') || rest.startsWith('.')
    })

  try {
    // Indexed rather than `Object.keys`: `Storage` is not required to expose
    // its entries as own properties, and the keys are collected before any
    // removal so the indexes cannot shift underneath the walk.
    const doomed: string[] = []
    for (let index = 0; index < window.localStorage.length; index++) {
      const key = window.localStorage.key(index)
      if (key && belongsToInterface(key)) doomed.push(key)
    }
    doomed.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Storage may be disabled by the browser; nothing local to clear.
  }
}
